/**
 * Logger Class
 * 
 * Main logger implementation with configurable output targets.
 */

import type { LogEntry, LogTarget, LoggerConfig } from './types.js';
import { LogLevel } from './types.js';
import { ConsoleTarget } from './targets/console.js';
import { StorageTarget } from './targets/storage.js';
import { RemoteTarget } from './targets/remote.js';

/**
 * Logger class
 */
export class Logger {
  private config: LoggerConfig;
  private targets: LogTarget[] = [];
  private levelOrder: Record<LogLevel, number> = {
    [LogLevel.Debug]: 0,
    [LogLevel.Info]: 1,
    [LogLevel.Warn]: 2,
    [LogLevel.Error]: 3,
  };

  constructor(config: Partial<LoggerConfig> = {}) {
    this.config = {
      level: config.level || LogLevel.Debug,
      enableConsole: config.enableConsole !== false,
      enableStorage: config.enableStorage || false,
      storageRetention: config.storageRetention || 1000,
      enableRemote: config.enableRemote || false,
      remoteEndpoint: config.remoteEndpoint,
    };

    this.setupTargets();
  }

  /**
   * Setup output targets based on configuration
   */
  private setupTargets(): void {
    if (this.config.enableConsole) {
      this.targets.push(new ConsoleTarget());
    }

    if (this.config.enableStorage) {
      this.targets.push(new StorageTarget(this.config.storageRetention));
    }

    if (this.config.enableRemote && this.config.remoteEndpoint) {
      this.targets.push(new RemoteTarget(this.config.remoteEndpoint));
    }
  }

  /**
   * Check if log level should be output
   */
  private shouldLog(level: LogLevel): boolean {
    return this.levelOrder[level] >= this.levelOrder[this.config.level];
  }

  /**
   * Write log entry to all targets
   */
  private write(entry: LogEntry): void {
    if (!this.shouldLog(entry.level)) {
      return;
    }

    for (const target of this.targets) {
      try {
        target.write(entry);
      } catch (error) {
      }
    }
  }

  /**
   * Log debug message
   */
  debug(message: string, metadata?: Record<string, unknown>, traceId?: string, source?: string): void {
    this.write({
      timestamp: Date.now(),
      level: LogLevel.Debug,
      message,
      metadata,
      traceId,
      source,
    });
  }

  /**
   * Log info message
   */
  info(message: string, metadata?: Record<string, unknown>, traceId?: string, source?: string): void {
    this.write({
      timestamp: Date.now(),
      level: LogLevel.Info,
      message,
      metadata,
      traceId,
      source,
    });
  }

  /**
   * Log warning message
   */
  warn(message: string, metadata?: Record<string, unknown>, traceId?: string, source?: string): void {
    this.write({
      timestamp: Date.now(),
      level: LogLevel.Warn,
      message,
      metadata,
      traceId,
      source,
    });
  }

  /**
   * Log error message
   */
  error(message: string, metadata?: Record<string, unknown>, traceId?: string, source?: string): void {
    this.write({
      timestamp: Date.now(),
      level: LogLevel.Error,
      message,
      metadata,
      traceId,
      source,
    });
  }

  /**
   * Clear all logs (if target supports it)
   */
  clear(): void {
    for (const target of this.targets) {
      if (target.clear) {
        target.clear();
      }
    }
  }

  /**
   * Get storage target (if enabled)
   */
  getStorageTarget(): StorageTarget | null {
    return this.targets.find(t => t instanceof StorageTarget) as StorageTarget | null;
  }

  /**
   * Update configuration
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
    this.targets = [];
    this.setupTargets();
  }
}

